// =====================================================================
// TypeFormModal.jsx - Create/edit PalletType (M-005 B2.2)
// =====================================================================
import { useState, useEffect } from 'react';
import { palletTypes as typesApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalStyle = {
  background: '#fff', borderRadius: 8, padding: '1.5rem 1.75rem',
  width: '90%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
};
const labelStyle = {
  display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#374151',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem',
};
const inputStyle = {
  width: '100%', padding: '0.5rem 0.625rem', border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: '0.875rem', background: '#fff', boxSizing: 'border-box',
};
const btnPrimary = { background: '#2563eb', color: '#fff', border: 0, padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };
const btnDanger = { background: 'transparent', color: '#b91c1c', border: '1px solid #fca5a5', padding: '0.5rem 1.25rem', borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer' };

export default function TypeFormModal({ typeId, onClose, onSaved }) {
  const { can } = useAuth();
  const isEdit = !!typeId;

  const canCreate = can('inventory.create.all');
  const canUpdate = can('inventory.update.all');
  const canDelete = can('inventory.delete.all');

  const [code, setCode]         = useState('');
  const [label, setLabel]       = useState('');
  const [notes, setNotes]       = useState('');
  const [isActive, setIsActive] = useState(true);
  const [movementsCount, setMovCount] = useState(0);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!isEdit) { setLoading(false); return; }
    async function load() {
      try {
        const t = await typesApi.get(typeId);
        if (cancelled) return;
        setCode(t.code);
        setLabel(t.label);
        setNotes(t.notes || '');
        setIsActive(t.isActive);
        setMovCount(t._count?.movements ?? 0);
      } catch (err) {
        console.error('[TypeFormModal] load:', err);
        setError(err.response?.data?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [typeId, isEdit]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    if (!code.trim()) return setError('Code requis');
    if (!label.trim()) return setError('Libellé requis');

    setSaving(true);
    try {
      const data = {
        code: code.trim().toUpperCase(),
        label: label.trim(),
        notes: notes.trim() || null,
        isActive,
      };
      if (isEdit) await typesApi.update(typeId, data);
      else        await typesApi.create(data);
      onSaved();
    } catch (err) {
      console.error('[TypeFormModal] save:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Supprimer le type ${code} ?\n\nNote: si le type a des mouvements associés, il sera désactivé.`)) return;
    setSaving(true);
    try {
      await typesApi.remove(typeId);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 1.25rem', fontSize: '1.25rem', fontWeight: 600 }}>
          {isEdit ? 'Modifier type' : 'Nouveau type de palette'}
        </h2>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.625rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Code * (ex: EURO, PJ, P120)</label>
            <input
              type="text"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              style={{ ...inputStyle, fontFamily: 'monospace', textTransform: 'uppercase' }}
              maxLength={20}
              disabled={saving || isEdit}
              required
              placeholder="EURO"
            />
            {isEdit && <small style={{ color: '#9ca3af', fontSize: '0.6875rem' }}>Le code ne peut pas être changé.</small>}
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Libellé *</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              style={inputStyle}
              maxLength={100}
              disabled={saving}
              required
              placeholder="Ex: Palette EUR 80x120 (standard)"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
              maxLength={500}
              disabled={saving}
            />
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} disabled={saving} />
              Type actif
            </label>
          </div>

          {isEdit && movementsCount > 0 && (
            <div style={{ background: '#fef3c7', color: '#92400e', padding: '0.5rem 0.75rem', borderRadius: 4, fontSize: '0.75rem', marginBottom: '1rem' }}>
              ⚠ {movementsCount} mouvement(s) lié(s). Suppression = désactivation seulement.
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {isEdit && canDelete && (
                <button type="button" style={btnDanger} onClick={handleDelete} disabled={saving}>Supprimer</button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>Annuler</button>
              {(canCreate || (isEdit && canUpdate)) && (
                <button type="submit" style={btnPrimary} disabled={saving}>
                  {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
